import { ReactNode, RefObject, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { ExternalSortManagement, SortStateDescriptor } from "components/Filters/types";
import Box from "ds/components/Box";
import { TableSimpleSelectionStore } from "ds/components/TableSimple/types";
import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";

import { SELECTION_EXTRA_WIDTH } from "../../constants";
import useColumnSort from "../../hooks/useColumnSort";
import { useResizer } from "../../hooks/useResizer";
import useTableWidth from "../../hooks/useTableWidth";
import { TableGenericStore } from "../../store/useTableStore/types";
import { SelectAllOption, TableColumnMeta } from "../../types";
import TableHeadColumn from "../Column";
import ColumnActionsDropdown from "./ActionsDropdown";
import { COLUMN_CHANGED_ANALYTIC_EVENT } from "./constants";
import TableHeadInteractiveColumnSelection from "./Selection";
import TableHeadInteractiveColumnTitle from "./Title";
import { useColumnWidth } from "./useColumnWidth";

export type TableHeadInteractiveColumnProps = {
  column: TableColumnMeta;
  store: TableGenericStore;
  selectionStore?: TableSimpleSelectionStore;
  selectionOptions?: SelectAllOption[];
  children?: ReactNode;
  stickyLeft?: boolean;
  stickyRight?: boolean;
  sortConfig?: Record<string, string>;
  sortCallback?: (sortDescriptor: SortStateDescriptor) => void;
  filtersStorageKey?: string;
  tableRef?: RefObject<HTMLDivElement | null>;
  analyticsPage?: AnalyticsPage;
  isLast?: boolean;
  externalSortManagement?: ExternalSortManagement;
  isPinningEnabled?: boolean;
};

const TableHeadInteractiveColumn = ({
  column,
  store,
  selectionStore,
  selectionOptions,
  children,
  stickyLeft,
  stickyRight,
  sortConfig,
  sortCallback,
  filtersStorageKey,
  tableRef,
  analyticsPage,
  isLast,
  externalSortManagement,
  isPinningEnabled,
}: TableHeadInteractiveColumnProps) => {
  const {
    hideColumn,
    columnsSizes,
    setColumnSize,
    columnsOrder,
    reorderVisibleColumns,
    pinColumn,
  } = store(
    useShallow((state) => ({
      hideColumn: state.hideColumn,
      columnsSizes: state.columnsSizes,
      setColumnSize: state.setColumnSize,
      columnsOrder: state.columnsOrder,
      reorderVisibleColumns: state.reorderVisibleColumns,
      pinColumn: state.pinColumn,
    }))
  );

  const trackAnalytics = useAnalytics({ page: analyticsPage });
  const tableWidth = useTableWidth(tableRef, !!stickyLeft);

  const withSelection = !!selectionStore && !!selectionOptions?.length;

  const { savedColumnWidth, effectiveMaxWidth } = useColumnWidth(
    column,
    columnsSizes,
    setColumnSize,
    stickyLeft,
    tableWidth
  );

  const {
    ariaSort,
    handleSort,
    icon: sortIcon,
  } = useColumnSort(
    sortConfig?.[column.id] || column.id,
    filtersStorageKey,
    externalSortManagement
  );

  const selectionWidth = withSelection ? SELECTION_EXTRA_WIDTH : 0;

  const { ResizerControl, columnRef } = useResizer({
    isLast,
    width: savedColumnWidth + selectionWidth,
    minWidth: column.minWidth + selectionWidth,
    maxWidth: effectiveMaxWidth + selectionWidth,
    onResizeClb: useCallback(
      (width: number) => {
        trackAnalytics(COLUMN_CHANGED_ANALYTIC_EVENT, {
          column: column.id,
          action: "resize",
        });
        setColumnSize(column.id, width - selectionWidth);
      },
      [column.id, setColumnSize, trackAnalytics, selectionWidth]
    ),
  });

  const onSort = useCallback(() => {
    handleSort(sortCallback);
  }, [handleSort, sortCallback]);

  return (
    <TableHeadColumn
      ref={columnRef}
      column={column}
      stickyLeft={stickyLeft}
      stickyRight={stickyRight}
      ariaSort={ariaSort}
      extraWidth={selectionWidth}
    >
      {children}
      {!children && column.noHeader && <span className="sr-only">{column.title}</span>}
      {!children && !column.noHeader && (
        <Box gap="small" align="center" justify="between" fullHeight>
          <Box direction="row" align="center" justify="between" gap="xx-large">
            {withSelection && (
              <TableHeadInteractiveColumnSelection
                selectionStore={selectionStore}
                selectionOptions={selectionOptions}
              />
            )}

            <TableHeadInteractiveColumnTitle
              title={column.title}
              icon={column.icon}
              iconOnly={column.iconOnly}
              allowsSorting={column.allowsSorting}
              sortIcon={sortIcon}
              onSort={onSort}
            />
          </Box>

          <Box align="center">
            <ColumnActionsDropdown
              column={column}
              columnsOrder={columnsOrder}
              stickyLeft={stickyLeft}
              hideColumn={hideColumn}
              pinColumn={pinColumn}
              reorderVisibleColumns={reorderVisibleColumns}
              analyticsPage={analyticsPage}
              isPinningEnabled={isPinningEnabled}
            />
            {column.resizable && ResizerControl}
          </Box>
        </Box>
      )}
    </TableHeadColumn>
  );
};

TableHeadInteractiveColumn.displayName = "DS.Table.Head.InteractiveColumn";

export default TableHeadInteractiveColumn;
